// Import auth service for authenticated requests
import { authService } from './authService';
import { getServerURL } from './config';

const SERVER_URL = getServerURL();

export const generationsService = {
  // Get all generations (paginated)
  async getAllGenerations(params = {}) {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }
    try {
      const {
        page = 1,
        limit = 20,
        status,
        issue,
        story_slug,
        generation_id,
        kid_name,
        q,
        dateFilterType,
        startDate,
        endDate,
        order_source,
        exclude_order_source,
      } = params;

      const searchParams = new URLSearchParams();
      if (page) searchParams.set('page', String(page));
      if (limit) searchParams.set('limit', String(limit));
      if (status && status !== 'all') searchParams.set('status', status);
      if (issue) searchParams.set('issue', issue);
      if (story_slug) searchParams.set('story_slug', story_slug);
      if (generation_id) searchParams.set('generation_id', generation_id);
      if (kid_name) searchParams.set('kid_name', kid_name);
      if (q) searchParams.set('q', q);
      if (dateFilterType) searchParams.set('dateFilterType', dateFilterType);
      if (startDate) searchParams.set('startDate', startDate);
      if (endDate) searchParams.set('endDate', endDate);
      if (order_source) searchParams.set('order_source', order_source);
      if (exclude_order_source) searchParams.set('exclude_order_source', exclude_order_source);

      const url = `${SERVER_URL}/generations${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;

      const response = await fetch(url, {
        headers: authService.getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch generations: ${response.status}`);
      }
      const result = await response.json();
      // Return the full response including pagination metadata if provided by API
      return result;
    } catch (error) {
      console.error('Error fetching generations:', error);
      return {
        success: false,
        total: 0,
        count: 0,
        page: 1,
        limit: 20,
        pageCount: 0,
        hasNext: false,
        hasPrev: false,
        database: 'GenerationStories',
        collection: 'Generations',
        data: [],
      };
    }
  },

  // Get generation by ID
  async getGenerationById(id) {
    try {
      const response = await fetch(`${SERVER_URL}/generation/${id}`, {
        headers: authService.getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        return data.data || null;
      } else {
        throw new Error(`Failed to fetch generation by ID: ${response.status}`);
      }
    } catch (error) {
      console.error('Error fetching generation by ID:', error);
      return null;
    }
  },

  // Get Generation authority with its server-confirmed country namespace.
  async getGenerationAuthorityByGenerationId(generationId) {
    try {
      const response = await fetch(`${SERVER_URL}/generations/${generationId}`, {
        headers: authService.getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        return data.data ? {
          generation: data.data,
          country: data.country || response.headers?.get('X-Country') || null,
        } : null;
      } else {
        throw new Error(`Failed to fetch generation by Generation ID: ${response.status}`);
      }
    } catch (error) {
      console.error('Error fetching generation by Generation ID:', error);
      return null;
    }
  },

  // Backward-compatible Generation accessor.
  async getGenerationByGenerationId(generationId) {
    const authority = await this.getGenerationAuthorityByGenerationId(generationId);
    return authority?.generation || null;
  },

  // Download generation images by ID
  async downloadById(id) {
    try {
      const response = await fetch(`${SERVER_URL}/generations/download/${id}`, {
        headers: authService.getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        return data.data || null;
      } else {
        throw new Error(`Failed to download by ID: ${response.status}`);
      }
    } catch (error) {
      console.error('Error downloading by ID:', error);
      return null;
    }
  },

  // Download generation images by Generation ID
  async downloadByGenerationId(generationId) {
    try {
      const response = await fetch(`${SERVER_URL}/generations/download/generationid/${generationId}/`, {
        headers: authService.getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        return data.data || null;
      } else {
        throw new Error(`Failed to download by Generation ID: ${response.status}`);
      }
    } catch (error) {
      console.error('Error downloading by Generation ID:', error);
      return null;
    }
  },

  // Create a new generation (process)
  async createGeneration({
    slug,
    kid_name,
    kid_image,
    generation_id,
    order_id,
    generation_provider_override,
    generation_model_override,
  }) {
    try {
      const payload = {
        slug,
        kid_name,
        kid_image,
        generation_id,
        order_id,
      };
      if (generation_provider_override !== undefined) {
        payload.generation_provider_override = generation_provider_override;
      }
      if (generation_model_override !== undefined) {
        payload.generation_model_override = generation_model_override;
      }

      const response = await fetch(`${SERVER_URL}/generation-stories/process`, {
        method: 'POST',
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        let errorData = {};
        try {
          errorData = await response.json();
        } catch { }
        throw new Error(errorData.error || errorData.message || `Failed to create generation: ${response.status}`);
      }
    } catch (error) {
      console.error('Error creating generation:', error);
      throw error;
    }
  },

  // Get all generation stories
  async getAllGenerationStories() {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }
    try {
      const response = await fetch(`${SERVER_URL}/generation-stories`, {
        headers: authService.getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch generation stories: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching generation stories:', error);
      return [];
    }
  },

  // Get queue items data
  async getQueueItems(page = 1, limit = 20) {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }
    try {
      const url = new URL(`${SERVER_URL}/queue/items`);
      url.searchParams.append('page', page.toString());
      url.searchParams.append('limit', limit.toString());

      const response = await fetch(url.toString(), {
        headers: authService.getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch queue items: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching queue items:', error);
      throw error;
    }
  },

  // Get queue stats
  async getQueueStats() {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }
    try {
      const response = await fetch(`${SERVER_URL}/queue/stats`, {
        headers: authService.getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch queue stats: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching queue stats:', error);
      return [];
    }
  },

  // Retry failed job
  async retryJob(jobId, options = {}) {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }
    try {
      const { checkDependencies = true, force = false } = options;
      const response = await fetch(`${SERVER_URL}/queue/retry/${jobId}`, {
        method: 'POST',
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          checkDependencies,
          force,
        }),
      });
      if (!response.ok) {
        throw new Error(`Failed to retry job: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error retrying job:', error);
      throw error;
    }
  },

  // Delete generation by ID
  async deleteGeneration(id) {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }
    try {
      const response = await fetch(`${SERVER_URL}/generations/${id}`, {
        method: 'DELETE',
        headers: authService.getAuthHeaders(),
      });
      if (!response.ok) {
        let errorData = {};
        try {
          errorData = await response.json();
        } catch { }
        throw new Error(errorData.error || errorData.message || `Failed to delete generation: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error deleting generation:', error);
      throw error;
    }
  },

  // Add help request flag to order
  async updateShopifyOrderWithHelp(orderId, helpNotes, generationId = null) {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }

    // Remove # from order ID if present
    const cleanOrderId = orderId.replace("#", "");
    console.log(`Adding help request flag to order ${cleanOrderId}`);

    try {
      const response = await fetch(`${SERVER_URL}/orders/${cleanOrderId}/flags/help-request`, {
        method: 'POST',
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          description: helpNotes,
          generation_id: generationId,
        }),
      });

      if (!response.ok) {
        let errorData = {};
        try {
          errorData = await response.json();
        } catch { }
        throw new Error(errorData.error || errorData.message || `Failed to add help request flag: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error adding help request flag:', error);
      throw error;
    }
  },

  // Bulk update generation status from the administrative generations page
  async bulkManualUpdateStatus(ids, status) {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }
    try {
      const response = await fetch(`${SERVER_URL}/generations/bulk/status`, {
        method: 'PATCH',
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ids,
          status,
          mode: 'manual',
        }),
      });
      if (!response.ok) {
        let errorData = {};
        try {
          errorData = await response.json();
        } catch { }
        throw new Error(errorData.error || errorData.message || `Failed to bulk update status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error bulk updating generation status:', error);
      throw error;
    }
  },

  // =============================================================================
  // FACE GENERATION FLOW
  // =============================================================================

  // Trigger face generation for a generation
  async generateFace(generationId, kidImage, kidName = null) {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }
    try {
      const body = {
        generation_id: generationId,
        kid_image: kidImage,
      };

      // Only include kid_name if provided
      if (kidName && kidName.trim()) {
        body.kid_name = kidName.trim();
      }

      const response = await fetch(`${SERVER_URL}/generation-stories/generate-face`, {
        method: 'POST',
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        let errorData = {};
        try {
          errorData = await response.json();
        } catch { }
        throw new Error(errorData.error || errorData.message || `Failed to trigger face generation: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error triggering face generation:', error);
      throw error;
    }
  },

  // Approve face and continue generation
  async approveFace(generationId) {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }
    try {
      const response = await fetch(`${SERVER_URL}/generation-stories/${generationId}/approve-face`, {
        method: 'POST',
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        let errorData = {};
        try {
          errorData = await response.json();
        } catch { }
        throw new Error(errorData.error || errorData.message || `Failed to approve face: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error approving face:', error);
      throw error;
    }
  },

  // Regenerate face
  async regenerateFace(generationId, kidImage = null) {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }
    try {
      const body = kidImage ? { kid_image: kidImage } : {};
      const response = await fetch(`${SERVER_URL}/generation-stories/${generationId}/regenerate-face`, {
        method: 'POST',
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        let errorData = {};
        try {
          errorData = await response.json();
        } catch { }
        throw new Error(errorData.error || errorData.message || `Failed to regenerate face: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error regenerating face:', error);
      throw error;
    }
  },

  // Generate cover-only trial (uses the cover scene's selected Regeneration Model, or story/global default)
  async generateTrial(generationId, customModel = '') {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }
    try {
      const trialBody = { generationId };
      const trimmedModel = String(customModel || '').trim();
      if (trimmedModel) {
        trialBody.customModel = trimmedModel;
      }
      const response = await fetch(`${SERVER_URL}/generation-stories/${generationId}/generate-trial`, {
        method: 'POST',
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(trialBody),
      });
      if (!response.ok) {
        let errorData = {};
        try {
          errorData = await response.json();
        } catch { }
        throw new Error(errorData.error || errorData.message || `Failed to generate cover trial: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error generating cover trial:', error);
      throw error;
    }
  },

  // Continue generation for missing scenes (keeps already-generated scenes)
  async regenerateMissingScenes(generationId) {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }
    try {
      const response = await fetch(`${SERVER_URL}/generation-stories/regenerate-missing`, {
        method: 'POST',
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ generationId: generationId }),
      });
      if (!response.ok) {
        let errorData = {};
        try {
          errorData = await response.json();
        } catch { }
        throw new Error(errorData.error || errorData.message || `Failed to continue generation: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error continuing generation:', error);
      throw error;
    }
  },

  // =============================================================================
  // GIFT BOX GENERATION FLOW
  // =============================================================================

  // Generate gift box image for a generation
  async generateGiftBox(generationId) {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }
    try {
      const response = await fetch(`${SERVER_URL}/generations/${generationId}/generate-gift-box`, {
        method: 'POST',
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        let errorData = {};
        try {
          errorData = await response.json();
        } catch { }
        throw new Error(errorData.error || errorData.message || `Failed to generate gift box: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error generating gift box:', error);
      throw error;
    }
  },

  // Get gift box image for a generation
  async getGiftBox(generationId) {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }
    try {
      const response = await fetch(`${SERVER_URL}/generations/${generationId}/gift-box`, {
        headers: authService.getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error(`Failed to get gift box: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error getting gift box:', error);
      throw error;
    }
  },

  // =============================================================================
  // GIFT NOTE CARD GENERATION FLOW
  // =============================================================================

  // Generate gift note card PDF for a generation
  async generateGiftNote(generationId) {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }
    try {
      const response = await fetch(`${SERVER_URL}/generations/${generationId}/generate-gift-note`, {
        method: 'POST',
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        let errorData = {};
        try {
          errorData = await response.json();
        } catch { }
        throw new Error(errorData.error || errorData.message || `Failed to generate gift note: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error generating gift note:', error);
      throw error;
    }
  },

  // Get gift note status/PDF for a generation
  async getGiftNote(generationId) {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }
    try {
      const response = await fetch(`${SERVER_URL}/generations/${generationId}/gift-note`, {
        headers: authService.getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error(`Failed to get gift note: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error getting gift note:', error);
      throw error;
    }
  },
};
